// 利用者が選んだファイルを、取り込みの前処理が扱える「ファイル名と本文」の一覧へそろえるモジュール。
// zip は中のCSVを取り出し、CSVはそのまま読む。読めなかったものは理由を添えた不備として返す。
// 列や値の点検は masterCsvFiles が行うため、ここでは中身に踏み込まない。

import { isCsvFileName, isZipFileName } from "../../../../util/fileNameExtensions";
import { textEntriesOfZip } from "../../../../util/zipIo";
import type { MasterCsvFileError, MasterCsvSourceFile } from "./masterCsvFiles";

// 受け取ったファイル群を読み終えた結果。1つでも読めなければ不備が入る。
export type MasterCsvIntakeResult = { sourceFiles: MasterCsvSourceFile[]; errors: MasterCsvFileError[] };

// ファイル名だけを添えた不備。行や列を特定できる段階ではないため該当欄は空にする。
function fileLevelError(fileName: string, message: string): MasterCsvFileError {
  return { fileName, lineNumber: null, columnName: null, message };
}

// ファイルの中身を文字列として読む。文字コードは UTF-8 として解釈し、BOM の扱いは解析側に任せる。
function textOfFile(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("ファイルを読み取れませんでした。"));
    reader.readAsText(file);
  });
}

// zip 1つ分を展開する。展開できなかった場合は zip のファイル名を添えて理由を返す。
async function intakeOfZipFile(file: File): Promise<MasterCsvIntakeResult> {
  const readResult = await textEntriesOfZip(file);
  if (readResult.ok) {
    return { sourceFiles: readResult.entries, errors: [] };
  }
  return { sourceFiles: [], errors: readResult.errors.map((message) => fileLevelError(file.name, message)) };
}

// CSV 1つ分を読む。
async function intakeOfCsvFile(file: File): Promise<MasterCsvIntakeResult> {
  try {
    const text = await textOfFile(file);
    return { sourceFiles: [{ fileName: file.name, text }], errors: [] };
  } catch {
    return { sourceFiles: [], errors: [fileLevelError(file.name, "ファイルを読み取れませんでした。もう一度選び直してください。")] };
  }
}

const UNSUPPORTED_EXTENSION_MESSAGE = "取り込めるのはCSVファイルと zip ファイルだけです。拡張子を確かめてください。";

async function intakeOfFile(file: File): Promise<MasterCsvIntakeResult> {
  if (isZipFileName(file.name)) {
    return await intakeOfZipFile(file);
  }
  if (isCsvFileName(file.name)) {
    return await intakeOfCsvFile(file);
  }
  return { sourceFiles: [], errors: [fileLevelError(file.name, UNSUPPORTED_EXTENSION_MESSAGE)] };
}

// 選ばれたファイルをすべて読み、取り込みの前処理へ渡せる形にまとめる。
// 1件目の不備で止めず最後まで読む。利用者が1回の修正でまとめて直せるようにするため。
export async function intakeOfFiles(files: readonly File[]): Promise<MasterCsvIntakeResult> {
  const sourceFiles: MasterCsvSourceFile[] = [];
  const errors: MasterCsvFileError[] = [];
  for (const file of files) {
    const result = await intakeOfFile(file);
    sourceFiles.push(...result.sourceFiles);
    errors.push(...result.errors);
  }
  if (files.length > 0 && sourceFiles.length === 0 && errors.length === 0) {
    errors.push(fileLevelError("", "取り込めるファイルがありませんでした。"));
  }
  return { sourceFiles, errors };
}
